import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { Label, Radio, RadioGroup } from 'react-aria-components';
import { Badge, type BadgeTone } from '../../components/Badge.tsx';
import { Banner } from '../../components/Banner.tsx';
import { Button } from '../../components/Button.tsx';
import { Checkbox } from '../../components/Checkbox.tsx';
import { TextField } from '../../components/TextField.tsx';
import { Disclosure } from '../../components/Disclosure.tsx';
import { can } from '../auth/permissions.ts';
import { useAuthorizations } from './controller.ts';
import {
  date,
  localDate,
  hosts,
  domainScopeDomains,
  message,
  authorizerErrors,
  TESTING_LEVELS,
  TESTING_LEVEL_NAMES,
  TESTING_LEVEL_SHORT,
  TESTING_LEVEL_LIMITS,
  testingLevelRank,
  type AuthorizerField,
  type AuthorizationConfig,
  type AuthorizationInput,
  type Letter,
  type TestingLevel,
} from './model.ts';
import { LetterDetails } from './LetterDetails.tsx';
import { DomainScopeCards } from './DomainScopeCards.tsx';
import { DomainPublication } from './DomainPublication.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import type { WorkspaceSession } from '../../state/session.ts';
import { StepHeading } from '../onboarding/StepHeading.tsx';
import styles from './authorization.module.css';

export function AuthorizationPage({
  pendingWorkspace,
  onSent,
  dnsConsent,
}: {
  pendingWorkspace?: WorkspaceSession | null;
  onSent?: () => void;
  dnsConsent?: ReactNode;
}) {
  const state = useAuthorizations(pendingWorkspace);
  const onboarding = Boolean(pendingWorkspace);
  const [openedAt] = useState(Date.now);
  const existing = state.letters.find((l) => !l.revokedAt);
  const pending =
    existing && (existing.confirmedAt || Date.parse(existing.terms.endsAt) > openedAt)
      ? existing
      : undefined;
  // The letter in force leads the page; everything else is history.
  const current = state.letters.find((l) => l.confirmedAt && !l.revokedAt) ?? state.letters[0];
  // One renewal awaits email approval (newest first); unapproved drafts are not history.
  const awaiting = state.letters.find((l) => l !== current && !l.confirmedAt && !l.revokedAt);
  const history = state.letters.filter((l) => l !== current && l.confirmedAt);
  const [review, setReview] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const canRenew = !onboarding && can(state.workspace, 'workspace.authorization.manage');
  const [localError, setLocalError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<AuthorizerField, string>>>({});
  const [form, setForm] = useState(() => ({
    engagement: `${state.workspace?.name ?? ''} domain authorization`,
    name: state.workspace?.authorizer?.name ?? '',
    email: state.workspace?.authorizer?.email ?? '',
    role: '',
    authority: '',
    exclusions: '',
    ipAddresses: '',
    testingLevel: 'PASSIVE' as TestingLevel,
    // In force as soon as setup finishes; a later start is the owner's choice to make here.
    startsAt: localDate(new Date()),
    endsAt: localDate(new Date(Date.now() + 24 * 3600000)),
    policyEndsAt: localDate(new Date(Date.now() + 30 * 86400000)),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    exclusive: true,
    ownershipDeclared: false,
    publicDisclosureApproved: false,
    impactAccepted: false,
  }));
  const profiles: AuthorizationConfig['profiles'] = state.config?.profiles?.length
    ? state.config.profiles
    : TESTING_LEVELS.map((id) => ({ id, name: TESTING_LEVEL_NAMES[id], description: '' }));
  const level = profiles.find((p) => p.id === form.testingLevel);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === 'endsAt' && typeof value === 'string' && value > next.policyEndsAt) {
        next.policyEndsAt = value;
      }
      return next;
    });
  // A signed letter cannot change; renewing prefills a new one from the latest confirmed terms.
  function startRenewal() {
    const t = state.letters.find((l) => l.confirmedAt)?.terms;
    setForm((f) => ({
      ...f,
      ...(t && {
        engagement: t.engagement,
        name: t.authorizer.name,
        email: t.authorizer.email,
        role: t.authorizer.role,
        authority: t.authorizer.authority,
        exclusions: t.exclusions.join(', '),
        ipAddresses: (t.ipAddresses ?? []).join(', '),
        testingLevel: t.testingLevel,
        exclusive: t.exclusive,
      }),
      startsAt: localDate(new Date()),
    }));
    setReview(false);
    setRenewing(true);
  }
  function input(): AuthorizationInput {
    const startsAt = new Date(form.startsAt).toISOString();
    const endsAt = new Date(form.endsAt).toISOString();
    const policyEndsAt = form.policyEndsAt ? new Date(form.policyEndsAt).toISOString() : endsAt;
    return {
      ...form,
      domains: domainScopeDomains(state.workspace?.domain ?? ''),
      exclusions: hosts(form.exclusions),
      ipAddresses:
        testingLevelRank(form.testingLevel) >= testingLevelRank('ACTIVE')
          ? hosts(form.ipAddresses)
          : [],
      // Sent only when true: older backends refuse unknown fields.
      impactAccepted: (form.testingLevel === 'HIGH_IMPACT' && form.impactAccepted) || undefined,
      startsAt,
      endsAt,
      policyEndsAt: policyEndsAt < endsAt ? endsAt : policyEndsAt,
    };
  }
  function prepare(e: FormEvent) {
    e.preventDefault();
    setLocalError('');
    try {
      const errors = authorizerErrors(form);
      setFieldErrors(errors);
      const first = Object.keys(errors)[0];
      if (first) {
        document.getElementById(`authorization-${first}`)?.focus();
        return;
      }
      const value = input();
      if (
        ![form.engagement, form.name, form.email, form.role, form.authority].every((v) => v.trim())
      )
        throw new Error('Complete the authorization and authorizer fields before reviewing.');
      if (Date.parse(value.endsAt) <= Date.parse(value.startsAt))
        throw new Error('The end time must be after the start time.');
      if (form.testingLevel === 'HIGH_IMPACT' && !form.impactAccepted)
        throw new Error('Accept the possible impact of high-impact testing before reviewing.');
      if (!form.ownershipDeclared || !form.publicDisclosureApproved)
        throw new Error('Confirm your authority and public disclosure before reviewing.');
      setReview(true);
    } catch (error) {
      setLocalError(message(error));
    }
  }
  async function send() {
    try {
      if (await state.create(input())) {
        setReview(false);
        setRenewing(false);
        onSent?.();
      }
    } catch (e) {
      setLocalError(message(e));
    }
  }
  return (
    <div className={`${styles.page} ${onboarding ? styles.onboarding : ''}`}>
      <header>
        {onboarding ? (
          <StepHeading
            title="Prepare your authorization"
            lead="Set the full mandate now. Confirm the exact terms by email before entering your workspace."
          />
        ) : (
          <div className={styles.header}>
            <div className={styles.intro}>
              <h1>Authorization</h1>
              <p className={styles.lead}>
                What Traceira may test on {state.workspace?.domain ?? 'your domain'}, and until
                when. To extend it, change it or add scope, send a new letter: it replaces this one
                once approved by email.
              </p>
            </div>
            {canRenew && !renewing && (
              <Button
                variant="primary"
                onPress={startRenewal}
                isDisabled={state.loading || !state.config?.available}
              >
                Extend or change scope
              </Button>
            )}
          </div>
        )}
        {onboarding && (
          <Disclosure title="How authorization works">
            <p>
              DNS proves domain control. Email records the person’s approval. Traceira signs the
              exact letter and publishes a separate status summary.
            </p>
            <p>
              The owner’s policy asks other testers to obtain permission. Active testing execution,
              monitoring, and protection require separate engagement mandates. Every letter can
              expire or be revoked; public checks refresh DNS and show the latest observation.
            </p>
            {dnsConsent}
          </Disclosure>
        )}
      </header>
      {(state.error || localError) && (
        <p role="alert" className={styles.error}>
          {localError || state.error}
        </p>
      )}
      {state.notice && (
        <p role="status" className={styles.notice}>
          {state.notice}
        </p>
      )}
      {state.loading ? (
        <p role="status">Loading authorization records…</p>
      ) : (
        <>
          {!state.config?.available && (
            <Banner tone="attention" title="Letter signing is unavailable">
              New letters cannot be sent until the service returns. Approved letters stay in force.
            </Banner>
          )}
          {(onboarding || renewing) && (
            <div className={`${styles.grid} ${renewing ? styles.panel : ''}`}>
              {onboarding && pending ? (
                <section className={styles.panel}>
                  <h2>Continue with your recorded letter</h2>
                  <p>
                    The exact letter is saved for {pending.terms.authorizer.email}. Check its
                    confirmation or email delivery state in the next step.
                  </p>
                  <Button variant="primary" onPress={onSent}>
                    Continue to email confirmation
                  </Button>
                </section>
              ) : (
                <form
                  className={`${styles.letterForm} ${review ? styles.reviewForm : ''}`}
                  onSubmit={prepare}
                >
                  <h2 className={review || renewing ? undefined : 'sr-only'}>
                    {review
                      ? 'Review before sending'
                      : renewing
                        ? 'Extend or change scope'
                        : 'Prepare an authorization letter'}
                  </h2>
                  {review ? (
                    <>
                      <p>
                        The email recipient will confirm these exact terms. Editing after approval
                        requires a new letter.
                      </p>
                      <LetterDetails
                        terms={{
                          ...input(),
                          organization: state.workspace?.name ?? '',
                          domain: state.workspace?.domain ?? '',
                          authorizer: {
                            name: form.name,
                            email: form.email,
                            role: form.role,
                            authority: form.authority,
                          },
                          limits: {
                            requestsPerSecond:
                              TESTING_LEVEL_LIMITS[form.testingLevel].requestsPerSecond,
                            concurrency: TESTING_LEVEL_LIMITS[form.testingLevel].concurrency,
                            // The backend signs its own list; this previews it.
                            prohibited: level?.prohibited ?? [],
                          },
                        }}
                      />
                      <div className={styles.formActions}>
                        <Button
                          variant="primary"
                          onPress={() => {
                            void send();
                          }}
                          isDisabled={state.busy || !state.config?.available}
                        >
                          {state.busy ? 'Sending review email…' : 'Send letter for email approval'}
                        </Button>
                        <Button onPress={() => setReview(false)} isDisabled={state.busy}>
                          Edit terms
                        </Button>
                        {renewing && (
                          <Button
                            variant="tertiary"
                            onPress={() => setRenewing(false)}
                            isDisabled={state.busy}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <fieldset className={styles.group}>
                        <legend>1. Authorization and scope</legend>
                        <div className={styles.fields}>
                          <TextField
                            label="Authorization name"
                            id="authorization-engagement"
                            error={fieldErrors.engagement}
                            value={form.engagement}
                            onChange={(v) => set('engagement', v)}
                          />
                        </div>
                        {/* Every level stays visible as a short chip; the chosen one is explained
                            below, in the space the old dropdown used (the step fits one screen). */}
                        <RadioGroup
                          className={styles.levels}
                          value={form.testingLevel}
                          onChange={(v) => set('testingLevel', v as TestingLevel)}
                        >
                          <Label className={styles.levelsLabel}>Testing level</Label>
                          <div className={styles.levelChips}>
                            {profiles.map((p) => (
                              <Radio
                                key={p.id}
                                value={p.id}
                                aria-label={p.name}
                                className={styles.level}
                              >
                                <span className={styles.levelTick} aria-hidden="true">
                                  <Check size={14} strokeWidth={3} />
                                </span>
                                <strong className={styles.levelName}>
                                  {TESTING_LEVEL_SHORT[p.id] ?? p.name}
                                </strong>
                              </Radio>
                            ))}
                          </div>
                          {level && (
                            <p className={styles.levelText}>
                              <strong>{level.name}</strong> ·{' '}
                              {TESTING_LEVEL_LIMITS[level.id].requestsPerSecond} requests/s,{' '}
                              {TESTING_LEVEL_LIMITS[level.id].concurrency} concurrent.{' '}
                              {level.description}
                            </p>
                          )}
                        </RadioGroup>
                        {form.testingLevel === 'HIGH_IMPACT' && (
                          <Checkbox
                            isSelected={form.impactAccepted}
                            onChange={(v) => set('impactAccepted', v)}
                          >
                            I accept in writing that high-impact testing may disrupt or damage the
                            included hosts, and I have arranged backups and a recovery plan.
                          </Checkbox>
                        )}
                        <div className={styles.fields}>
                          <div className={styles.scopeField}>
                            <span className={styles.scopeFieldLabel}>Included domain scope</span>
                            <DomainScopeCards
                              domains={domainScopeDomains(state.workspace?.domain ?? '')}
                              domain={state.workspace?.domain ?? ''}
                            />
                            <p className={styles.note}>
                              Includes the verified apex and every subdomain at any depth via the
                              listed wildcard. Listed exclusions remain outside scope; third-party
                              infrastructure still requires its owner’s permission.
                            </p>
                          </div>
                          <TextField
                            label="Excluded hosts"
                            description="Optional, comma-separated."
                            value={form.exclusions}
                            onChange={(v) => set('exclusions', v)}
                            mono
                          />
                        </div>
                        {testingLevelRank(form.testingLevel) >= testingLevelRank('ACTIVE') && (
                          <div className={styles.fields}>
                            <TextField
                              label="Exact included IP addresses"
                              description="Comma-separated. Only addresses you operate; active checks probe these exactly. No CDN or shared infrastructure."
                              value={form.ipAddresses}
                              onChange={(v) => set('ipAddresses', v)}
                              mono
                            />
                          </div>
                        )}
                        <div className={styles.fields}>
                          <TextField
                            label="Starts at · local time"
                            type="datetime-local"
                            value={form.startsAt}
                            onChange={(v) => set('startsAt', v)}
                          />
                          <TextField
                            label="Ends at · local time"
                            type="datetime-local"
                            value={form.endsAt}
                            onChange={(v) => set('endsAt', v)}
                          />
                        </div>
                        <p className={styles.note}>
                          Timezone: {form.timezone}.
                          {state.config?.grantMaxHours
                            ? ` Maximum testing window: ${state.config.grantMaxHours} hours.`
                            : ''}
                        </p>
                      </fieldset>
                      <fieldset className={styles.group}>
                        <legend>2. Owner authorization</legend>
                        <div className={styles.fields}>
                          <TextField
                            label="Full name"
                            id="authorization-name"
                            error={fieldErrors.name}
                            isReadOnly={Boolean(state.workspace?.authorizer)}
                            value={form.name}
                            onChange={(v) => set('name', v)}
                            autoComplete="name"
                          />
                          <TextField
                            label="Role"
                            id="authorization-role"
                            error={fieldErrors.role}
                            value={form.role}
                            onChange={(v) => set('role', v)}
                            autoComplete="organization-title"
                          />
                        </div>
                        <TextField
                          label="Business email"
                          isReadOnly={Boolean(state.workspace?.authorizer)}
                          type="email"
                          description={
                            state.workspace?.authorizer
                              ? 'Your setup email. The letter goes here for review and approval.'
                              : `Use an address at @${state.workspace?.domain ?? ''}. A review link confirms access to this mailbox.`
                          }
                          value={form.email}
                          onChange={(v) => set('email', v)}
                          autoComplete="email"
                        />
                        <TextField
                          label="Basis of authority"
                          id="authorization-authority"
                          error={fieldErrors.authority}
                          description="Describe your responsibility for these assets. Email access alone does not establish legal authority."
                          value={form.authority}
                          onChange={(v) => set('authority', v)}
                        />
                        <Checkbox
                          isSelected={form.ownershipDeclared}
                          onChange={(v) => set('ownershipDeclared', v)}
                        >
                          I represent the owner and may authorize this scope. It contains no assets
                          that require another party’s permission.
                        </Checkbox>
                      </fieldset>
                      <fieldset className={styles.group}>
                        <legend>3. Public provider policy</legend>
                        <Checkbox isSelected={form.exclusive} onChange={(v) => set('exclusive', v)}>
                          Authorize Traceira as the exclusive testing provider during this policy
                          period.
                        </Checkbox>
                        <TextField
                          label="Provider policy ends at · local time"
                          type="datetime-local"
                          value={form.policyEndsAt}
                          onChange={(v) => set('policyEndsAt', v)}
                          description="This may outlast the testing window. It grants no permission to continue testing after that window."
                        />
                        <Checkbox
                          isSelected={form.publicDisclosureApproved}
                          onChange={(v) => set('publicDisclosureApproved', v)}
                        >
                          Publish the hostnames, testing window, level, and provider policy. Keep my
                          name, email, and private letter out of public responses.
                        </Checkbox>
                      </fieldset>
                      <div className={styles.formActions}>
                        <p className={styles.note}>
                          Review the exact letter before sending it for email approval.
                        </p>
                        <Button
                          type="submit"
                          variant="primary"
                          isDisabled={state.busy || !state.config?.available}
                        >
                          Review letter
                        </Button>
                        {renewing && (
                          <Button variant="tertiary" onPress={() => setRenewing(false)}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>
          )}
          {!onboarding && current && (
            <LetterCard
              letter={current}
              current
              busy={state.busy}
              onResend={() => void state.resend(current.id)}
              onRefresh={() => void state.refresh()}
            />
          )}
          {!onboarding && awaiting && (
            <section className={styles.list} aria-labelledby="awaiting-title">
              <div className={styles.header}>
                <h2 id="awaiting-title">Waiting for email approval</h2>
              </div>
              <p className={styles.note}>
                This renewal replaces the current letter once the authorizer approves it from the
                latest email. Sending a new letter withdraws this one.
              </p>
              <LetterCard
                letter={awaiting}
                busy={state.busy}
                onResend={() => void state.resend(awaiting.id)}
              />
            </section>
          )}
          {!onboarding && (history.length > 0 || !current) && (
            <section className={styles.list} aria-labelledby="letters-title">
              <div className={styles.header}>
                <h2 id="letters-title">
                  {current ? 'Earlier letters' : 'Letters'} · {history.length}
                </h2>
                <Button
                  variant="tertiary"
                  onPress={() => {
                    void state.refresh();
                  }}
                  isDisabled={state.busy}
                >
                  Refresh
                </Button>
              </div>
              {!state.letters.length && (
                <p className={styles.note}>
                  No letters were returned. Refresh to load the authorization completed during
                  onboarding.
                </p>
              )}
              {history.map((l) => (
                <LetterCard
                  key={l.id}
                  letter={l}
                  busy={state.busy}
                  onResend={() => void state.resend(l.id)}
                />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}

const STATUS: Record<string, { label: string; tone: BadgeTone; shape: string }> = {
  active: { label: 'Active', tone: 'positive', shape: '✓' },
  scheduled: { label: 'Scheduled', tone: 'neutral', shape: '○' },
  pending: { label: 'Awaiting email approval', tone: 'attention', shape: '△' },
  unsent: { label: 'Email not sent', tone: 'attention', shape: '△' },
  unavailable: { label: 'DNS proof stale', tone: 'attention', shape: '△' },
  suspended: { label: 'Suspended · DNS proof failed', tone: 'attention', shape: '△' },
  expired: { label: 'Expired', tone: 'neutral', shape: '×' },
  revoked: { label: 'Revoked', tone: 'negative', shape: '×' },
};

function relative(iso: string) {
  const hours = Math.round((Date.parse(iso) - Date.now()) / 3600000);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  return Math.abs(hours) < 48
    ? rtf.format(hours, 'hour')
    : rtf.format(Math.round(hours / 24), 'day');
}

function windowNote(terms: Letter['terms']) {
  const now = Date.now();
  if (now < Date.parse(terms.startsAt)) return `Starts ${relative(terms.startsAt)}`;
  if (now < Date.parse(terms.endsAt)) return `Ends ${relative(terms.endsAt)}`;
  return `Ended ${relative(terms.endsAt)}`;
}

function LetterCard({
  letter,
  current = false,
  busy,
  onResend,
  onRefresh,
}: {
  letter: Letter;
  current?: boolean;
  busy: boolean;
  onResend: () => void;
  onRefresh?: () => void;
}) {
  const t = letter.terms;
  const unsent = !letter.confirmedAt && letter.emailSentAt === null;
  const status = STATUS[unsent ? 'unsent' : letter.status] ?? {
    label: letter.status,
    tone: 'neutral' as const,
    shape: '○',
  };
  const Heading = current ? 'h2' : 'h3';
  return (
    <article
      className={`${styles.panel} ${current ? styles.currentLetter : ''}`}
      aria-label={current ? 'Current authorization' : t.engagement}
    >
      <div className={styles.header}>
        <div className={styles.intro}>
          {current && <p className={styles.kicker}>Current authorization</p>}
          <Heading>{t.engagement}</Heading>
        </div>
        <Badge tone={status.tone} shape={status.shape} data-badge={letter.status}>
          {status.label}
        </Badge>
      </div>
      <dl className={styles.facts}>
        <div>
          <dt>Testing level</dt>
          <dd>
            <strong>{TESTING_LEVEL_NAMES[t.testingLevel as TestingLevel] ?? t.testingLevel}</strong>
            <span>
              {t.limits.requestsPerSecond} requests/s · {t.limits.concurrency} concurrent
            </span>
          </dd>
        </div>
        <div>
          <dt>Testing window</dt>
          <dd>
            <strong>
              {date(t.startsAt)} → {date(t.endsAt)}
            </strong>
            <span>{windowNote(t)}</span>
          </dd>
        </div>
        <div>
          <dt>Scope</dt>
          <dd>
            <DomainScopeCards domains={t.domains} domain={t.domain} />
            <span>
              {t.ipAddresses?.length ? `IPs ${t.ipAddresses.join(', ')} · ` : ''}
              {t.exclusions.length ? `Excludes ${t.exclusions.join(', ')}` : 'No exclusions'}
            </span>
          </dd>
        </div>
        <div>
          <dt>Approved by</dt>
          <dd>
            <strong>{t.authorizer.name}</strong>
            <span>
              {t.authorizer.role} · {t.authorizer.email}
            </span>
          </dd>
        </div>
      </dl>
      <Disclosure title="Full letter terms">
        <LetterDetails terms={t} />
      </Disclosure>
      <div className={styles.actions}>
        {letter.confirmedAt ? (
          <Link className={buttonClass('secondary')} to="/owner/$id" params={{ id: letter.id }}>
            Owner controls
          </Link>
        ) : (
          <Button onPress={onResend} isDisabled={busy}>
            {unsent ? 'Send approval email' : 'Resend approval email'}
          </Button>
        )}
        {onRefresh && (
          <Button variant="tertiary" onPress={onRefresh} isDisabled={busy}>
            Refresh status
          </Button>
        )}
      </div>
      {letter.confirmedAt && !letter.revokedAt && (
        <DomainPublication letter={letter} collapseWhenDone />
      )}
    </article>
  );
}
